import React, { useEffect, useMemo, useState } from "react";
import posthog from "posthog-js";
import { Card, CardHeader, CardTitle } from "../../components/ui/Card";
import { Table } from "../../components/ui/Table";
import { Input } from "../../components/ui/Input";
import { Button } from "../../components/ui/Button";
import { DropdownMenu } from "../../components/ui/DropdownMenu";
import { Icon } from "../../components/ui/Icon";
import { useCustomers } from "../../hooks/useCustomers";
import { buildPublicCarteUrl, DEFAULT_PUBLIC_CARTE_ID } from "../../lib/publicCarte";
import { addCustomersBulk, createCustomer } from "./customersPage.api";
import { CustomerCsvImportModal } from "./components/CustomerCsvImportModal";
import { CustomerManualImportModal } from "./components/CustomerManualImportModal";
import type { Customer, CustomerInput } from "./customers.types";

function normalizeText(value: string): string {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function scoreCustomer(customer: Customer, query: string): number {
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) return 1;

  const fields = [customer.name, customer.email, customer.phone || "", customer.city, customer.country, customer.address]
    .map(normalizeText)
    .filter(Boolean);

  const joined = fields.join(" ");
  if (joined === normalizedQuery) return 200;

  let score = 0;
  for (const field of fields) {
    if (field.startsWith(normalizedQuery)) score += 120;
    else if (field.includes(normalizedQuery)) score += 80;
  }

  const tokens = normalizedQuery.split(/\s+/).filter(Boolean);
  for (const token of tokens) {
    for (const field of fields) {
      if (field.startsWith(token)) score += 18;
      else if (field.includes(token)) score += 10;
    }
  }

  return score;
}

function createCarteMailto(customer: Customer, carteId = DEFAULT_PUBLIC_CARTE_ID): string {
  const publicUrl = buildPublicCarteUrl({
    customerHash: customer.publicCarteHash,
    userId: customer.userId,
    carteId,
  });
  const subject = "Votre carte des vins";
  const body = [
    `Bonjour ${customer.name},`,
    "",
    "Voici le lien vers votre carte des vins :",
    publicUrl,
    "",
    "Bonne lecture.",
  ].join("\n");

  const search = new URLSearchParams({
    subject,
    body,
  });

  return `mailto:${customer.email}?${search.toString()}`;
}

export function CustomersPage() {
  const { customers, loading, fetchCustomers } = useCustomers();
  const [query, setQuery] = useState("");
  const [showManualImport, setShowManualImport] = useState(false);
  const [showCsvImport, setShowCsvImport] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    fetchCustomers().catch(() => {});
  }, [fetchCustomers]);

  const filteredCustomers = useMemo(() => {
    const rows = customers
      .map((customer) => ({ customer, score: scoreCustomer(customer, query) }))
      .filter(({ score }) => !query.trim() || score > 0)
      .sort((left, right) => {
        if (right.score !== left.score) return right.score - left.score;
        return left.customer.name.localeCompare(right.customer.name, "fr");
      })
      .map(({ customer }) => customer);

    return rows;
  }, [customers, query]);

  const handleManualImport = async (input: CustomerInput) => {
    setSubmitting(true);
    setFeedback(null);
    try {
      await createCustomer(input);
      await fetchCustomers();
      setShowManualImport(false);
      setFeedback(`Client ajouté : ${input.name}`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCsvImport = async (rows: CustomerInput[]) => {
    setSubmitting(true);
    setFeedback(null);
    try {
      const result = await addCustomersBulk(rows);
      await fetchCustomers();
      setFeedback(`${result.created.length} client${result.created.length > 1 ? "s" : ""} importé${result.created.length > 1 ? "s" : ""}.`);
      return result;
    } finally {
      setSubmitting(false);
    }
  };

  const openCarteMail = (
    customer: Customer,
    carteId = DEFAULT_PUBLIC_CARTE_ID
  ) => {
    posthog.capture("carte_public_mail_shared", {
      customer_hash: customer.publicCarteHash,
      public_carte_owner_user_id: customer.userId,
      carte_id: carteId,
      delivery_channel: "mailto",
    });

    const mailtoUrl = createCarteMailto(customer, carteId);
    const link = document.createElement("a");
    link.href = mailtoUrl;
    link.rel = "noopener noreferrer";
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      key: "name",
      header: "Client",
      render: (customer: Customer) => (
        <div>
          <div className="font-semibold text-foreground">{customer.name}</div>
          <div className="text-[12px] text-secondary">{customer.email}</div>
        </div>
      ),
    },
    {
      key: "phone",
      header: "Téléphone",
      render: (customer: Customer) => customer.phone || <span className="text-tertiary">—</span>,
    },
    {
      key: "city",
      header: "Ville",
      render: (customer: Customer) => (
        <div>
          <div>{customer.city}</div>
          <div className="text-[12px] text-secondary">{customer.postalCode}</div>
        </div>
      ),
    },
    {
      key: "country",
      header: "Pays",
      render: (customer: Customer) => customer.country,
    },
    {
      key: "address",
      header: "Adresse",
      render: (customer: Customer) => <span className="line-clamp-2">{customer.address}</span>,
      className: "min-w-[220px]",
    },
    {
      key: "share",
      header: "Carte",
      className: "w-[150px]",
      render: (customer: Customer) => (
        <div className="flex justify-end">
          <DropdownMenu
            trigger={
              <Button type="button" variant="ghost" size="sm" className="inline-flex items-center gap-2 whitespace-nowrap">
                <Icon name="note" size={14} />
                Envoyer
              </Button>
            }
            positioning={{ placement: "bottom-end" }}
            items={[
              {
                value: DEFAULT_PUBLIC_CARTE_ID,
                label: "Carte principale",
                icon: <Icon name="arrow-right" size={14} />,
                onSelect: () => openCarteMail(customer),
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="mx-auto flex h-full w-full flex-1 flex-col gap-5 px-5 py-8 lg:px-8">
      
        <CustomerManualImportModal
          open={showManualImport}
          onClose={() => !submitting && setShowManualImport(false)}
          onSubmit={handleManualImport}
        />


        <CustomerCsvImportModal
          open={showCsvImport}
          onClose={() => !submitting && setShowCsvImport(false)}
          onImport={handleCsvImport}
        />

<div className="flex flex-row gap-5">s
        <Card className="p-5">
          <CardHeader className="mb-4 items-start gap-3">
            <div>
              <CardTitle className="text-[13px] tracking-[0.2em] text-tertiary">Répertoire client</CardTitle>
              <h2 className="mt-2 font-display text-[30px] font-bold tracking-[-0.05em] text-foreground">
                {customers.length} client{customers.length > 1 ? "s" : ""} relié{customers.length > 1 ? "s" : ""} à votre espace
              </h2>
              <p className="mt-2 max-w-[640px] text-sm leading-[1.7] text-secondary">
                Recherchez vos clients par nom, email, téléphone ou adresse et importez-les en un seul geste depuis le panneau d'ajout.
              </p>
            </div>

            <DropdownMenu
              trigger={
                <Button type="button" variant="accent" className="inline-flex items-center gap-2 whitespace-nowrap">
                  <Icon name="user" size={15} />
                  Ajouter
                </Button>
              }
              positioning={{ placement: "bottom-end" }}
              items={[
                {
                  value: "manual",
                  label: "Import manuel",
                  icon: <Icon name="edit" size={14} />,
                  onSelect: () => setShowManualImport(true),
                },
                {
                  value: "csv",
                  label: "Import CSV",
                  icon: <Icon name="csv" size={14} />,
                  onSelect: () => setShowCsvImport(true),
                },
              ]}
            />
          </CardHeader>

          <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_200px]">
            <Input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Recherche par nom, email, téléphone, ville..."
              aria-label="Rechercher un client"
            />
            <div className="rounded-[18px] border border-border bg-surface/70 px-4 py-3 text-sm text-secondary">
              {query.trim()
                ? `${filteredCustomers.length} résultat${filteredCustomers.length > 1 ? "s" : ""} proche${filteredCustomers.length > 1 ? "s" : ""}`
                : `${customers.length} client${customers.length > 1 ? "s" : ""} au total`}
            </div>
          </div>

          {feedback && <div className="mt-3 text-sm text-[#2E8B57]">{feedback}</div>}

          <div className="mt-5">
            <Table
              data={filteredCustomers}
              columns={columns}
              keyExtractor={(customer) => customer.id}
              emptyState={loading ? "Chargement des clients..." : "Aucun client trouvé pour cette recherche."}
            />
          </div>
        </Card>

        <Card className="p-5">
          <div className="rounded-[20px] border border-border bg-[linear-gradient(180deg,rgba(46,139,87,0.12),rgba(46,139,87,0.02))] p-4">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#2E8B57]/20 bg-[#2E8B57]/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#2E8B57]">
              Flux d'import
            </div>
            <h3 className="mt-3 font-display text-[24px] font-bold tracking-[-0.04em] text-foreground">
              Deux entrées, une table propre
            </h3>
            <p className="mt-2 text-sm leading-[1.7] text-secondary">
              L'ajout manuel permet une saisie immédiate. Le CSV accélère les imports volumineux et ignore les doublons déjà connus pour votre compte.
            </p>
          </div>

          <div className="mt-4 space-y-3 text-sm text-secondary">
            <div className="rounded-[18px] border border-border bg-surface/65 p-4">
              <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-tertiary">Import manuel</div>
              <div className="mt-1 leading-[1.7]">Nom, email, téléphone optionnel, ville, code postal, pays et adresse.</div>
            </div>
            <div className="rounded-[18px] border border-border bg-surface/65 p-4">
              <div className="text-[12px] font-semibold uppercase tracking-[0.16em] text-tertiary">Import CSV</div>
              <div className="mt-1 leading-[1.7]">Prévisualisation avant envoi, validation des colonnes requises et doublons ignorés automatiquement.</div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}